import {Contact} from "../contact/ContactProvider";
import {BulkSyncResult, BulkSyncSummary, FluentCRMTag} from "./FluentCRMTypes";
import {generateSlug, sleep} from "../../system/utils";
import {FluentCrmProvider} from "./FluentCrmProvider";
import {HealingDefinition} from "../definitions/HealingDefinitionProvider";
import {ClassDefinition} from "../definitions/ClassDefinitionProvider";

export interface CreateFluentCRMTag {
    title: string;
    slug?: string;        // optional → will be auto-generated if missing
    description?: string; // optional
}

function chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}

export class FluentCrmSynchronizer {
    private readonly provider: FluentCrmProvider;

    public StateChanged: number = 0;

    constructor(provider: FluentCrmProvider) {
        this.provider = provider;
    }

    public async syncClassDefinitionsWithTags(
        classDefinitions: ClassDefinition[],
        saveClass: (healing: ClassDefinition) => Promise<string>,
        onStatusChange: (message: string) => void
    ): Promise<{
        created: FluentCRMTag[];
        existing: FluentCRMTag[];
        failed: { title: string; error: string }[];
    }> {

        // first validate all healings have a
        // slug since we added this feature later
        for (const classDef of classDefinitions) {
            if (!classDef.slug) {
                classDef.slug = generateSlug(classDef.title);
                await saveClass(classDef);
                console.log(`Added slug to class ${classDef.title} (${classDef.slug})`);
            }
        }

        // Build input list for bulkCreateTags
        const classTags: CreateFluentCRMTag[] = classDefinitions.map(c => ({
            title: c.title,
            description: `Tag from class: ${c.title}`,
            slug: c.slug
        }));

        // Bulk create or resolve existing tags
        const {created, existing, failed} = await this.provider.bulkCreateTags(classTags, onStatusChange);

        return {created, existing, failed};
    }

    public async syncHealingDefinitionsWithTags(
        healings: HealingDefinition[],
        saveHealing: (healing: HealingDefinition) => Promise<string>,
        onStatusChange: (message: string) => void
    ): Promise<{
        created: FluentCRMTag[];
        existing: FluentCRMTag[];
        failed: { title: string; error: string }[];
    }> {

        // first validate all healings have a
        // slug since we added this feature later
        for (const healing of healings) {
            if (!healing.slug) {
                healing.slug = generateSlug(healing.title);
                await saveHealing(healing);
                onStatusChange(`Added slug to healing ${healing.title} (${healing.slug})`);
            }
        }

        // Build input list for bulkCreateTags
        const healingTags: CreateFluentCRMTag[] = healings.map(h => ({
            title: h.title,
            description: `Tag from the healing: ${h.title}`,
            slug: h.slug
        }));

        // Bulk create or resolve existing tags
        const {created, existing, failed} = await this.provider.bulkCreateTags(healingTags, onStatusChange);

        return {created, existing, failed};
    }


    public async bulkSyncAllContacts(
        contacts: Contact[],
        onStatusMessage: (message: string) => void
    ): Promise<{ result: BulkSyncResult, summary: BulkSyncSummary }> {
        const CHUNK_SIZE = 100; // safe default
        const chunks = chunkArray(contacts, CHUNK_SIZE);

        const aggregatedResult: BulkSyncResult = {created: [], updated: [], failed: []};

        for (let i = 0; i < chunks.length; i++) {

            const rawChunk = chunks[i];

            // detect undefined contacts
            const badEntries = rawChunk
                .map((c, idx) => (c == null ? idx : null))
                .filter((idx): idx is number => idx !== null);

            if (badEntries.length > 0) {
                onStatusMessage(
                    `⚠️ Chunk ${i + 1} contains ${badEntries.length} undefined contacts at positions [${badEntries.join(
                        ", "
                    )}]`
                );
            }

            // clean the chunk
            const chunk = rawChunk.filter((c): c is Contact => c != null);


            onStatusMessage(`Syncing chunk ${i + 1} of ${chunks.length} (${chunk.length} contacts)...`);

            const {result} = await this.provider.bulkSyncContacts(chunk);
            aggregatedResult.created.push(...result.created);
            aggregatedResult.updated.push(...result.updated);
            aggregatedResult.failed.push(...result.failed);

            onStatusMessage(
                `Finished chunk ${i + 1}: +${result.created.length} created, +${result.updated.length} updated, +${result.failed.length} failed`
            );

            await sleep(1000);

        }

        // Build summary once at the end
        const summary: BulkSyncSummary = {
            createdCount: aggregatedResult.created.length,
            updatedCount: aggregatedResult.updated.length,
            failedCount: aggregatedResult.failed.length,
            createdEmails: aggregatedResult.created.map(c => c.email),
            updatedEmails: aggregatedResult.updated.map(c => c.email),
            failed: aggregatedResult.failed.map(f => ({
                email: f.contact.email,
                error: f.error
            }))
        };

        onStatusMessage(
            `✅ Bulk sync complete: ${summary.createdCount} created, ${summary.updatedCount} updated, ${summary.failedCount} failed`
        );

        return {result: aggregatedResult, summary};
    }


}
